import { useGetPickPlanQuery } from 'common/api/endpoints/schedule';
import { handleError } from 'common/api/utils/handleError';
import { CsvHeaders } from 'common/components/CsvButton/CsvButton';
import { WithLoadingOverlay } from 'common/components/LoadingSpinner';
import { WeekSwitcherState } from 'common/components/TimeframeSwitcher/useTimeframeSwitcherState';
import WithTooltip from 'common/components/WithTooltip/WithTooltip';
import { Variety } from 'common/models';
import { BinRequest } from 'common/models/binRequest';
import { DateOnlyFormat } from 'common/models/dateType';
import { roundBins } from 'common/models/harvestData/utilities';
import { PackHouse, PickPlanRowData } from 'common/models/packHouse';
import * as notifier from 'common/services/notification';
import { PlanViewContext } from 'features/pick-plan-views/pages/PickPlanView/PickPlanView';
import {
  CsvPlanHeaderKeys,
  prepareCsvPlanData,
} from 'features/pick-plan-views/utils/prepareCsvPlanData';
import { useRbac } from 'features/rbac';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Constants } from 'utils/constants';
import { DayInfo } from 'utils/dateTimeHelpers';
import { NewPackRowForm } from './NewPackRowForm';
import { PickPlanRow } from './PickPlanRow';
import {
  DateColumnContainer,
  FooterText,
  PackDate,
  PackDay,
  PHCode,
  RemoveRowBtnContainer,
  TableFooter,
  TableHeader,
  TableRow,
  TotalsColumnContainer,
  TotalsFooter,
  TotalsHeader,
} from './styles';

export type PickPlanRowCombo = {
  rowId: string;
} & PickPlanRowData;

export type NewRowFormData = {
  rowId: string;
  varietyId: number;
  market: string;
  sizeId: number;
};

export const PickPlanTable: FC<{
  packHouse: PackHouse;
  week: WeekSwitcherState;
  varieties: Variety[] | undefined;
  isLoadingVarieties: boolean;
  setCsvPlanData: React.Dispatch<
    React.SetStateAction<{
      headers: CsvHeaders<CsvPlanHeaderKeys>[];
      rows: Record<string, Record<string, string>[]>;
    }>
  >;
}> = ({ packHouse, week, varieties, isLoadingVarieties, setCsvPlanData }) => {
  const { monday, weekdays } = week;
  const {
    data: planData,
    isLoading: isLoadingPlan,
    isFetching: isFetchingPlan,
    error: fetchPlanError,
  } = useGetPickPlanQuery({
    packHouseId: packHouse.id,
    startDate: monday.format(DateOnlyFormat),
  });

  const { pickPlan, dayTotals } = planData ?? {};
  const { isSavingReq, setLoadingPlanCount } = useContext(PlanViewContext);
  const { userHasPermission } = useRbac();
  const columnTotals = weekdays.map(weekday => {
    const columnTotal = dayTotals?.find(
      ({ date }) => new Date(date).getUTCDate() === weekday.day,
    );

    return {
      date: `${weekday.month}/${weekday.day}`,
      total: columnTotal ? columnTotal.total : 0,
    };
  });
  const getEmptyReq = ({ year, month, day }: DayInfo): BinRequest => {
    // decrement needed to account for 0 basing in months
    const prevMonth = month - 1 < 0 ? 11 : month - 1;
    return {
      id: null,
      date: new Date(year, prevMonth, day).toString(),
      bins: 0,
    };
  };
  const [newRows, setNewRows] = useState([] as PickPlanRowCombo[]);

  const planRows = useMemo(() => {
    const formattedRows: PickPlanRowCombo[] | undefined = pickPlan?.map(
      ({
        variety,
        market,
        size,
        binRequests,
        totalRequested,
        totalPicked,
      }) => ({
        rowId: `${variety.id}${market}${size.id}`,
        variety,
        market,
        size,
        binRequests: weekdays.map(weekday => {
          const binReq = binRequests.find(
            req => weekday.day === new Date(req.date).getUTCDate(),
          );
          if (!binReq) return getEmptyReq(weekday);
          return binReq;
        }),
        totalRequested,
        totalPicked,
      }),
    );
    return [...(formattedRows || []), ...newRows].sort((a, b) => {
      let result = a.variety.varietyCode.localeCompare(b.variety.varietyCode);
      if (result === 0) result = a.market.localeCompare(b.market);
      if (result === 0) result = a.size.value.localeCompare(b.size.value);
      return result;
    });
  }, [newRows, pickPlan, weekdays]);

  const reqPickedTotals = useMemo(() => {
    const totals = { totalRequested: 0, totalPicked: 0 };
    pickPlan?.forEach(row => {
      totals.totalRequested += row.totalRequested;
      totals.totalPicked += row.totalPicked;
    });
    return totals;
  }, [pickPlan]);

  const addNewRow = ({ varietyId, market, sizeId }: NewRowFormData) => {
    const rowId = `${varietyId}${market}${sizeId}`;
    const comboExists = !!planRows.find(row => row.rowId === rowId);
    const targetVariety = varieties?.find(vty => vty.id === varietyId);
    const targetSize = targetVariety?.sizes.find(size => size.id === sizeId);

    if (comboExists) {
      notifier.showErrorMessage(Constants.errorMessages.COMBINATION_EXISTS);
    } else if (!targetVariety) {
      notifier.showErrorMessage('Variety not available.');
    } else if (!targetSize) {
      notifier.showErrorMessage('Size not available.');
    } else {
      setNewRows([
        ...newRows,
        {
          rowId,
          variety: targetVariety,
          market,
          size: targetSize,
          binRequests: weekdays.map(weekday => getEmptyReq(weekday)) as Extract<
            PickPlanRowCombo,
            'binRequests'
          >,
          totalRequested: 0,
          totalPicked: 0,
        },
      ]);
    }
  };

  const removeNewRow = (rowId: string) =>
    setNewRows([...newRows.filter(row => row.rowId !== rowId)]);

  // When querying the plan, inform parent of loading state.
  useEffect(() => {
    if (isLoadingPlan || isFetchingPlan) {
      setLoadingPlanCount(prev => prev + 1);
    } else {
      setLoadingPlanCount(prev => (prev > 0 ? prev - 1 : prev));
    }
  }, [isLoadingPlan, isFetchingPlan, setLoadingPlanCount]);

  // On plan load, update parent component of new data for csv file
  useEffect(() => {
    const { headers, rows } = prepareCsvPlanData(
      week,
      packHouse.code,
      planRows,
      columnTotals,
      reqPickedTotals,
    );
    setCsvPlanData(data => ({
      headers,
      rows: {
        ...data.rows,
        ...{
          [`${rows[0].pHouseCode}`]: rows,
        },
      },
    }));
    // Subscribe only to pick plan changes as all other values derive from it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickPlan]);

  useEffect(() => {
    if (fetchPlanError) {
      handleError(
        fetchPlanError,
        `Unable to load ${packHouse.name} Pick Plan.`,
      );
    }
  }, [fetchPlanError, packHouse.name]);

  return (
    <>
      <TableHeader>
        <PHCode>
          <WithTooltip title='Pack House' tooltipText={packHouse.name}>
            <span>{packHouse.code}</span>
          </WithTooltip>
        </PHCode>
        {weekdays.map(({ weekday, month, day }) => (
          <DateColumnContainer key={`${month}/${day}`}>
            <PackDay>{weekday}</PackDay>
            <PackDate>{`${month}/${day}`}</PackDate>
          </DateColumnContainer>
        ))}
        <RemoveRowBtnContainer />
        <TotalsColumnContainer>
          <TotalsHeader>Total Sales</TotalsHeader>
          <TotalsHeader>Total Pick</TotalsHeader>
        </TotalsColumnContainer>
      </TableHeader>

      <WithLoadingOverlay
        isLoading={isLoadingPlan || isFetchingPlan || isSavingReq}
        size='2em'
      >
        {planRows.map(row => (
          <PickPlanRow
            key={row.rowId}
            packHouse={packHouse}
            row={row}
            newRows={newRows}
            removeNewRow={removeNewRow}
          />
        ))}
      </WithLoadingOverlay>

      {userHasPermission('pickPlan:create-row') && (
        <TableRow>
          <NewPackRowForm
            id={packHouse.id}
            varieties={varieties}
            isLoading={isLoadingVarieties}
            onAddRowReq={addNewRow}
          />
        </TableRow>
      )}

      <TableFooter>
        <FooterText>Totals</FooterText>
        {columnTotals.map(({ date, total }) => (
          <DateColumnContainer key={date} className='day-total'>
            <span>{total || '00'}</span>
          </DateColumnContainer>
        ))}
        <RemoveRowBtnContainer />
        <TotalsColumnContainer>
          <TotalsFooter>{reqPickedTotals.totalRequested || '00'}</TotalsFooter>
          <TotalsFooter>
            {roundBins(reqPickedTotals.totalPicked) || '00'}
          </TotalsFooter>
        </TotalsColumnContainer>
      </TableFooter>
    </>
  );
};
